import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import client from '../shopify';
import { useNavigate } from 'react-router-dom';
import { enhanceCheckoutUrl } from '../utils/shopifyUtils';

const CartContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100%;
  background-color: #fff;
  box-shadow: -5px 0 15px rgba(0,0,0,0.1);
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-in-out;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const CartHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #fff; /* match background to hide line */
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin: 0;
    font-size: 1.2em;
  }

  button {
    background: none;
    border: none;
    font-size: 1.5em;
    cursor: pointer;
  }
`;

const CartBody = styled.div`
  flex-grow: 1;
  padding: 20px;
  overflow-y: auto;
`;

const CartFooter = styled.div`
  padding: 20px;
  border-top: 1px solid #fff; /* match background to hide line */
`;

const CheckoutButton = styled.button`
  display: block;
  width: 100%;
  padding: 15px;
  background-color: #000;
  color: #fff;
  text-align: center;
  text-decoration: none;
  font-size: 1em;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
  }
`;

const EmptyCartMessage = styled.p`
  text-align: center;
  color: #666;
`;

const LineItemContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const LineItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  margin-right: 15px;
  cursor: pointer;
`;

const LineItemDetails = styled.div`
  flex-grow: 1;

  h4 {
    margin: 0 0 5px 0;
    font-size: 1em;
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
  }

  p {
    margin: 0;
    font-size: 0.9em;
    color: #666;
  }
`;

const Cart = ({ isOpen: $isOpen, onClose }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchCart = useCallback(async () => {
    const cartId = localStorage.getItem('cartId');
    if (!cartId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const fetchPromise = client.request(
        `query getCart($cartId: ID!) {
          cart(id: $cartId) {
            id
            checkoutUrl
            estimatedCost {
              totalAmount {
                amount
                currencyCode
              }
            }
            lines(first: 250) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        src
                      }
                      priceV2: price {
                        amount
                        currencyCode
                      }
                      product {
                        title
                        handle
                      }
                    }
                  }
                }
              }
            }
          }
        }`,
        { variables: { cartId } }
      );
      
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out')), 10000);
      });
      
      const { data } = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (data && data.cart) {
        setCart(data.cart);
      } else {
        throw new Error('Invalid cart data received');
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      setError(error.message || 'Failed to load cart data');
      
      if (error.message && (error.message.includes('cart') || error.message.includes('not found'))) {
        localStorage.removeItem('cartId');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleCartUpdate = () => {
      fetchCart();
      // Auto-open cart when items are added
      if (onClose && typeof onClose === 'function') {
        // This assumes onClose is actually a toggle function
        // If it's strictly for closing, you'll need to modify App.js
        onClose(true); // Force open
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [fetchCart, onClose]);

  useEffect(() => {
    if ($isOpen) {
      fetchCart();
    }
  }, [$isOpen, fetchCart]);

  const removeLineItem = async (lineId) => {
    const cartId = localStorage.getItem('cartId');
    if (!cartId) return;
    
    setLoading(true);
    try {
      const { data, errors } = await client.request(
        `mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart {
              id
            }
            userErrors {
              field
              message
            }
          }
        }`,
        { variables: { cartId, lineIds: [lineId] } }
      );
      
      if (errors || (data.cartLinesRemove.userErrors && data.cartLinesRemove.userErrors.length > 0)) {
        console.error('Error removing item:', errors || data.cartLinesRemove.userErrors);
        return;
      }
      
      // Refresh cart
      fetchCart();
    } catch (error) {
      console.error('Error removing item from cart:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCheckout = () => {
    if (cart && cart.checkoutUrl) {
      // Use the enhanced checkout URL with proper authentication
      const enhancedUrl = enhanceCheckoutUrl(cart.checkoutUrl);
      console.log('Redirecting to checkout:', enhancedUrl);
      window.location.href = enhancedUrl;
    }
  };
  
  const navigateToProduct = (handle) => {
    onClose(); // Close cart
    navigate(`/product/${handle}`);
  };

  return (
    <CartContainer $isOpen={$isOpen}>
      <CartHeader>
        <h2>Shopping Cart</h2>
        <button onClick={onClose}>&times;</button>
      </CartHeader>
      <CartBody>
        {loading ? (
          <EmptyCartMessage>Loading cart...</EmptyCartMessage>
        ) : error ? (
          <ErrorContainer>
            <ErrorMessage>{error}</ErrorMessage>
            <RetryButton onClick={fetchCart}>Try Again</RetryButton>
          </ErrorContainer>
        ) : cart && cart.lines.edges.length > 0 ? (
          cart.lines.edges.map(({ node: item }) => (
            <LineItemContainer key={item.id}>
              <LineItemImage 
                src={item.merchandise.image?.src} 
                alt={item.merchandise.product.title} 
                onClick={() => navigateToProduct(item.merchandise.product.handle)}
              />
              <LineItemDetails>
                <h4 onClick={() => navigateToProduct(item.merchandise.product.handle)}>
                  {item.merchandise.product.title}
                </h4>
                <p>{item.merchandise.title}</p>
                <p>Quantity: {item.quantity}</p>
                <p>${parseFloat(item.merchandise.priceV2.amount).toFixed(2)}</p>
                <RemoveButton onClick={() => removeLineItem(item.id)}>Remove</RemoveButton>
              </LineItemDetails>
            </LineItemContainer>
          ))
        ) : (
          <EmptyCartMessage>Your cart is empty.</EmptyCartMessage>
        )}
      </CartBody>
      {cart && cart.lines.edges.length > 0 && (
        <CartFooter>
          <TotalContainer>
            <span>Total:</span>
            <span>${cart.estimatedCost?.totalAmount ? parseFloat(cart.estimatedCost.totalAmount.amount).toFixed(2) : '0.00'}</span>
          </TotalContainer>
          <CheckoutButton onClick={handleCheckout}>
            Proceed to Checkout
          </CheckoutButton>
        </CartFooter>
      )}
    </CartContainer>
  );
};

const RemoveButton = styled.button`
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  color: #333;
  cursor: pointer;
  padding: 4px 8px;
  margin-top: 8px;
  font-size: 0.8em;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  
  &:hover {
    background-color: #e0e0e0;
    color: #000;
  }
  
  &:before {
    content: '×';
    margin-right: 4px;
    font-size: 1.1em;
  }
`;

const TotalContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  font-weight: bold;
  font-size: 1.1em;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
`;

const ErrorMessage = styled.p`
  color: #e53935;
  margin-bottom: 15px;
`;

const RetryButton = styled.button`
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 0.9em;
  
  &:hover {
    background-color: #e0e0e0;
  }
`;

export default Cart;
